import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { enWebPage } from "../lib/ConstCommand";
import { connectLiveForClassMonitor, disconnectRbmqForSmartTV, sendClassMonitorScreenState, setCurrentPageType, setRbmqInfoForClassMonitor } from "../lib/RabbitmqClient";
import { setTeacherElementForClassMonitor, subscribe, unsubscribe } from "../lib/MediaSoupClient";
import ClassMonitorVideoTile from "../components/live/classMonitor/ClassMonitorVideoTile";
import { get_class_monitor_live } from "../lib/api/user";
import { initClassMonitorGroupResetProc, initClassMonitorLiveOffProc, initClassMonitorNicknameProc, initClassMonitorUserScreenModeProc } from "../lib/rbmq/rcvMsgFromRbmq";
import { exitPageFullscreen, usePageFullscreen } from "../hooks/usePageFullscreen";
import "../styles/class-monitor.css";

const PAGE_SIZE = 20;

const normalizeLiveInfo = info => info ? ({
    liveSeq: info.LIVE_SEQ,
    liveName: info.LIVE_NAME,
    liveStatus: info.LIVE_STATUS,
    groupLimit: info.GROUP_LIMIT_NUM || 4,
    teacher_info: {
        userSeq: info.LIVE_TEACHER_SEQ,
        userNickname: info.LIVE_TEACHER_NICKNAME || info.USER_NICKNAME,
        userProfile: info.USER_PROFILE,
        profileImgUrl: info.USER_PROFILE_IMG
    },
    list_member: (info.list_member || []).map(member => ({
        userSeq: member.USER_SEQ,
        userNickname: member.USER_NICKNAME,
        userProfile: member.USER_PROFILE,
        profileImgUrl: member.USER_PROFILE_IMG,
        entryYN: member.ENTRY_YN
    }))
}) : null;

// 260728 전체 학생, SmartTV식 그룹 분할, 교사 중심 및 개별 확대를 제공한다.
const WebClassMonitorPage = ({ routeProps, isRbmqServerConnected, isReadyLiveConsume, performLiveStatusInfo, smartTVBindInfo }) => {
    const { t } = useTranslation("live");
    const [session] = useState(() => {
        try {
            const savedSession = JSON.parse(sessionStorage.getItem("classMonitorSession"));
            const entryState = routeProps.location && routeProps.location.state;
            const routeLiveSeq = routeProps.match && routeProps.match.params.liveSeq;
            const savedLiveSeq = savedSession && savedSession.live_info && savedSession.live_info.LIVE_SEQ;
            // 260728 로그인 화면의 클래스 선택으로 발급된 동일 세션·클래스인지 함께 검증한다.
            const isValidEntry = savedSession && entryState && entryState.fromClassMonitorLogin === true
                && entryState.monitorSessionId === savedSession.monitorSessionId
                && String(routeLiveSeq) === String(savedLiveSeq);
            return isValidEntry ? savedSession : null;
        } catch (err) {
            return null;
        }
    });
    const [liveInfo, setLiveInfo] = useState(() => normalizeLiveInfo(session && session.live_info));
    const [viewMode, setViewMode] = useState("all");
    const [page, setPage] = useState(1);
    const [groupIndex, setGroupIndex] = useState(0);
    const [groupUserIndexArr, setGroupUserIndexArr] = useState([]);
    const [selected, setSelected] = useState(null);
    const [objectFitMode, setObjectFitMode] = useState("contain"); // 260805 기존 모니터 화면 비율을 기본값으로 유지하며 표시 방식을 전환한다.
    const [screenStateVersion, setScreenStateVersion] = useState(0); // 260803 같은 상태 재요청에도 현재 상태 JSON을 다시 회신한다.
    const [teacherVideoEl, setTeacherVideoEl] = useState(null);
    const [teacherAudioEl, setTeacherAudioEl] = useState(null);
    const [teacherMicEl, setTeacherMicEl] = useState(null);
    const { isFullscreen, toggleFullscreen } = usePageFullscreen(true); // 260803 모니터 화면 진입 시 전체화면을 요청하고 상태를 버튼에 반영한다.
    const isLiveConnectRequestedRef = useRef(false);
    const isLiveOffAlertShownRef = useRef(false); // 260729 중복 LIVE_OFF 신호에도 종료 알림을 한 번만 표시한다.
    const allGridRef = useRef(null);
    const currentLiveSeq = liveInfo && liveInfo.liveSeq; // 260728 닉네임 콜백은 클래스가 바뀔 때만 재등록한다.

    useEffect(() => {
        setCurrentPageType(enWebPage.ClassMonitor); // 260728 세션 확인 전에도 공통 page type 이동 규칙을 확정한다.
        const sessionLiveInfo = normalizeLiveInfo(session && session.live_info);
        if (!session || !sessionLiveInfo) {
            sessionStorage.removeItem("classMonitorSession"); // 260728 직접 URL 진입에 남은 세션을 재사용하지 않는다.
            exitPageFullscreen(); // 260803 유효하지 않은 진입으로 로그인 화면에 돌아갈 때 전체화면을 해제한다.
            routeProps.history.replace("/class-monitor/login");
            return undefined;
        }
        // 260728 클래스 선택 진입 권한은 한 번만 사용하고 제거해 이후 새로고침을 로그인 화면으로 보낸다.
        routeProps.history.replace(routeProps.location.pathname, {});
        const rbmq = session.rbmq_info;
        const isHttpMonitor = window.location.protocol === "http:"; // 260728 HTTP는 WS, HTTPS는 WSS RabbitMQ 포트를 사용한다.
        const rbmqInfo = {
            exchangeName: rbmq.EXCH_NAME,
            ip: rbmq.SVER_IP,
            port: Number(rbmq.SVER_PORT_NEW),
            mqttPort: rbmq.MQTT_PORT ? Number(rbmq.MQTT_PORT) : null,
            wsIp: rbmq.WSVER_IP,
            wsPort: isHttpMonitor ? 15675 : Number(rbmq.WSVER_PORT || 15676),
            wsPath: rbmq.WSVER_PATH,
            vHost: rbmq.VRTL_HOST,
            userPW: rbmq.PSWD,
            userID: rbmq.USER_ID
        };
        setRbmqInfoForClassMonitor({
            receiverSeq: session.receiverSeq,
            monitorSessionId: session.monitorSessionId,
            monitorBindKey: session.monitorBindKey,
            list_member: sessionLiveInfo.list_member,
            rbmq_info: rbmqInfo,
            tempKey: session.tempKey,
            live_info: sessionLiveInfo
        });
        return () => {
            unsubscribe(false, true, true);
            // 260728 CM 고유 키를 포함한 다중 binding을 해제해 서버 세션 정리 이벤트를 발생시킨다.
            disconnectRbmqForSmartTV();
            sessionStorage.removeItem("classMonitorSession");
        };
    }, [routeProps.history, routeProps.location.pathname, session]);

    useEffect(() => {
        // 260728 Redux 갱신으로 동일 bind 정보가 다시 들어와도 임시 키 해제를 한 번만 요청한다.
        if (!isLiveConnectRequestedRef.current && smartTVBindInfo.bindKeyArr.length > 0 && smartTVBindInfo.startSeqOfSmartTV > -1 && smartTVBindInfo.serverGateWay) {
            isLiveConnectRequestedRef.current = true;
            setGroupUserIndexArr(smartTVBindInfo.groupUserIndexArr || []);
            connectLiveForClassMonitor(smartTVBindInfo.bindKeyArr, smartTVBindInfo.startSeqOfSmartTV, smartTVBindInfo.serverGateWay);
        }
    }, [smartTVBindInfo]);

    useEffect(() => {
        if (performLiveStatusInfo.isConnect && teacherVideoEl) {
            // 260728 교사 타일이 처음 나타날 때만 consume하고 이후 모드 전환에서는 기존 consumer를 재사용한다.
            const hasConsumer = setTeacherElementForClassMonitor(teacherVideoEl, teacherAudioEl, teacherMicEl);
            if (!hasConsumer) subscribe(teacherVideoEl, teacherAudioEl, teacherMicEl);
        }
    }, [performLiveStatusInfo.isConnect, teacherAudioEl, teacherMicEl, teacherVideoEl]);

    useEffect(() => {
        if (!liveInfo) return undefined;
        // 260728 그룹 재설정 신호에는 목록이 없어 최신 정보만 다시 조회하고 페이지는 유지한다.
        initClassMonitorGroupResetProc(information => {
            // 260728 client_new가 보낸 실제 그룹 인덱스를 우선 적용하고 영상 연결은 유지한다.
            if (information && Array.isArray(information.groupUserIndexArr)) setGroupUserIndexArr(information.groupUserIndexArr);
            get_class_monitor_live(liveInfo.liveSeq).then(response => {
                const nextInfo = normalizeLiveInfo(response.data.live_info);
                setLiveInfo(nextInfo);
                setGroupIndex(0);
                setSelected(null);
            });
        });
        return () => initClassMonitorGroupResetProc(null);
    }, [liveInfo]);

    useEffect(() => {
        if (currentLiveSeq === null || currentLiveSeq === undefined) return undefined;
        // 260729 기존 SmartTV처럼 클래스 종료를 알린 뒤 클래스 선택 로그인 화면으로 돌아간다.
        initClassMonitorLiveOffProc(liveSeq => {
            if (Number(liveSeq) !== Number(currentLiveSeq) || isLiveOffAlertShownRef.current) return;
            isLiveOffAlertShownRef.current = true;
            window.alert(t("클래스_모니터_클래스_종료_안내"));
            sessionStorage.removeItem("classMonitorSession");
            exitPageFullscreen(); // 260803 클래스 종료로 로그인 화면에 돌아갈 때 전체화면을 해제한다.
            routeProps.history.replace("/class-monitor/login");
        });
        return () => initClassMonitorLiveOffProc(null);
    }, [currentLiveSeq, routeProps.history, t]);

    useEffect(() => {
        if (currentLiveSeq === null || currentLiveSeq === undefined) return undefined;
        // 260728 학생이 다른 닉네임으로 로그인하면 영상 연결을 유지한 채 해당 타일 이름만 변경한다.
        initClassMonitorNicknameProc(userInfo => {
            if (!userInfo || Number(userInfo.liveSeq) !== Number(currentLiveSeq)) return;
            setLiveInfo(current => current ? {
                ...current,
                list_member: current.list_member.map(member => Number(member.userSeq) === Number(userInfo.userSeq)
                    ? { ...member, userNickname: userInfo.userNickname }
                    : member)
            } : current);
            setSelected(current => current && !current.isTeacher && Number(current.userSeq) === Number(userInfo.userSeq)
                ? { ...current, userNickname: userInfo.userNickname }
                : current);
        });
        return () => initClassMonitorNicknameProc(null);
    }, [currentLiveSeq]);

    useEffect(() => {
        if (!liveInfo) return undefined;
        // 260803 학생 JSON의 목표 상태에 따라 해당 학생만 명시적으로 최대화하거나 최소화한다.
        initClassMonitorUserScreenModeProc(information => {
            if (!information || Number(information.liveSeq) !== Number(liveInfo.liveSeq)) return;
            const member = liveInfo.list_member.find(user => Number(user.userSeq) === Number(information.userSeq));
            const isMaximized = information.isMaximized === true || information.isMaximized === "true" || Number(information.isMaximized) === 1;
            console.log(`setUserScreenMode - userSeq[${information.userSeq}] isMaximized[${isMaximized}] member[${!!member}]`);
            if (isMaximized) {
                if (member) setSelected(member);
            } else {
                setSelected(current => current && !current.isTeacher && Number(current.userSeq) === Number(information.userSeq) ? null : current);
            }
            setScreenStateVersion(current => current + 1);
        });
        return () => initClassMonitorUserScreenModeProc(null);
    }, [liveInfo]);

    useEffect(() => {
        if (!liveInfo || smartTVBindInfo.startSeqOfSmartTV < 0) return;
        // 260803 선택 변경 결과를 학생들에게 회신해 A가 B의 최대화 이후 다시 최대화 요청을 보낼 수 있게 한다.
        const maximizedUserSeq = selected && !selected.isTeacher ? selected.userSeq : -1;
        sendClassMonitorScreenState(liveInfo.liveSeq, smartTVBindInfo.startSeqOfSmartTV, maximizedUserSeq, liveInfo.list_member);
    }, [liveInfo, screenStateVersion, selected, smartTVBindInfo.startSeqOfSmartTV]);

    if (!liveInfo) return null;

    const allItems = [...liveInfo.list_member, { ...liveInfo.teacher_info, isTeacher: true }];
    const pageCount = Math.max(1, Math.ceil(allItems.length / PAGE_SIZE));
    const groupSize = [4, 6, 9].includes(Number(liveInfo.groupLimit)) ? Number(liveInfo.groupLimit) : 4;
    const groups = groupUserIndexArr.length > 0
        ? groupUserIndexArr.map(group => group.map(index => liveInfo.list_member[index]).filter(Boolean)).filter(group => group.length > 0)
        : [];
    if (groups.length === 0) {
        for (let i = 0; i < liveInfo.list_member.length; i += groupSize) groups.push(liveInfo.list_member.slice(i, i + groupSize));
    }
    const groupMembers = groups[groupIndex] || groups[0] || [];
    const selectedMode = selected ? 1 : viewMode === "group" ? 4 : 0;

    // 260728 전체 목록은 유지한 채 20명 단위 위치로 바로 스크롤한다.
    const moveAllGridPage = nextPage => {
        const grid = allGridRef.current;
        if (!grid) return;
        const normalizedPage = Math.min(Math.max(nextPage, 1), pageCount);
        const target = grid.querySelector(`[data-monitor-index="${(normalizedPage - 1) * PAGE_SIZE}"]`);
        const maxScrollTop = Math.max(0, grid.scrollHeight - grid.clientHeight);
        // 260728 마지막 페이지는 마지막 학생과 교사 화면까지 보이도록 목록 끝에 맞춘다.
        const targetTop = normalizedPage > 1 && normalizedPage === pageCount
            ? maxScrollTop
            : Math.min(target ? target.offsetTop - grid.offsetTop : 0, maxScrollTop);
        grid.scrollTo({ top: targetTop, behavior: "smooth" });
        setPage(normalizedPage);
    };

    const updateAllGridPage = event => {
        const grid = event.currentTarget;
        let currentPage = 1;
        const maxScrollTop = Math.max(0, grid.scrollHeight - grid.clientHeight);
        for (let index = 1; index < pageCount; index++) {
            const target = grid.querySelector(`[data-monitor-index="${index * PAGE_SIZE}"]`);
            const targetTop = index === pageCount - 1
                ? maxScrollTop
                : Math.min(target ? target.offsetTop - grid.offsetTop : 0, maxScrollTop);
            if (target && grid.scrollTop >= targetTop - 8) currentPage = index + 1;
        }
        if (currentPage !== page) setPage(currentPage);
    };

    const renderTeacher = (className, canSelect = true, key) => (
        <button key={key} type="button" data-monitor-index={liveInfo.list_member.length} className={`class-monitor-tile class-monitor-teacher ${className || ""}`} onClick={canSelect ? () => setSelected({ ...liveInfo.teacher_info, isTeacher: true }) : undefined}>
            <video ref={setTeacherVideoEl} poster="/images/connect.png" playsInline autoPlay muted />
            <audio ref={setTeacherAudioEl} autoPlay style={{ display: "none" }} />
            <audio ref={setTeacherMicEl} style={{ display: "none" }} />
            <span className="class-monitor-tile-name">{t("클래스_모니터_교사_이름", { name: liveInfo.teacher_info.userNickname })}</span>
        </button>
    );
    const visibleGroupUserSeq = new Set(groupMembers.map(user => user.userSeq));
    const isTeacherVisible = selected ? selected.isTeacher : viewMode !== "group";
    const getStudentView = user => {
        const isSelectedStudent = selected && !selected.isTeacher && selected.userSeq === user.userSeq;
        const isVisible = selected ? isSelectedStudent : viewMode !== "group" || visibleGroupUserSeq.has(user.userSeq);
        const mode = isSelectedStudent ? 1 : !selected && viewMode === "group" && isVisible ? 4 : 0;
        return { isVisible, mode };
    };
    const stageMode = selected ? "focus" : viewMode;
    const renderStudent = (user, index) => {
        const studentView = getStudentView(user);
        return <ClassMonitorVideoTile key={user.userSeq} index={index} user={user} liveSeq={liveInfo.liveSeq} mode={studentView.mode} isReady={isRbmqServerConnected && isReadyLiveConsume} onSelect={() => setSelected(user)} className={studentView.isVisible ? "" : "class-monitor-hidden"} />;
    };

    // 260728 SmartTV 뒤로가기처럼 연결을 정리하고 클래스 선택 페이지를 새로 연다.
    const onClickGoBack = () => {
        unsubscribe(false, true, true);
        disconnectRbmqForSmartTV();
        sessionStorage.removeItem("classMonitorSession");
        exitPageFullscreen(); // 260803 뒤로가기로 로그인 화면을 열기 전에 전체화면을 해제한다.
        window.location.replace("/class-monitor/login");
    };

    return (
        <main className={`class-monitor-page object-fit-${objectFitMode}`}>
            <header>
                <div className="class-monitor-title-area">
                    <button type="button" className="class-monitor-page-back" title={t("클래스_모니터_뒤로가기")} aria-label={t("클래스_모니터_뒤로가기")} onClick={onClickGoBack}>←</button>
                    <div><strong>{liveInfo.liveName}</strong><span>Class Monitor</span></div>
                </div>
                <span>{isRbmqServerConnected ? t("클래스_모니터_RABBITMQ_연결됨") : t("클래스_모니터_연결_중")}</span>
            </header>
            <nav>
                {/* 260728 확대 화면 복귀는 상세 화면 관례에 맞춰 메뉴 줄의 왼쪽 첫 위치에 둔다. */}
                {selected && <button type="button" className="class-monitor-nav-back" onClick={() => setSelected(null)}>← {t("클래스_모니터_이전_화면")}</button>}
                <button type="button" className={viewMode === "all" ? "active" : ""} onClick={() => { setViewMode("all"); setSelected(null); setPage(1); }}>{t("클래스_모니터_전체_학생")}</button>
                <button type="button" className={viewMode === "teacher" ? "active" : ""} onClick={() => { setViewMode("teacher"); setSelected(null); }}>{t("클래스_모니터_교사_중심")}</button>
                <button type="button" className={viewMode === "group" ? "active" : ""} onClick={() => { setViewMode("group"); setSelected(null); }}>{t("클래스_모니터_그룹_보기")}</button>
                {/* 260803 모니터링 모드 버튼 옆에 전체화면 ON/OFF 버튼을 배치한다. */}
                <button type="button" onClick={toggleFullscreen}>{isFullscreen ? t("클래스_모니터_전체화면_OFF") : t("클래스_모니터_전체화면_ON")}</button>
                {/* 260805 모니터링의 모든 교사·학생 영상에 같은 화면 맞춤 방식을 적용한다. */}
                <button type="button" onClick={() => setObjectFitMode(current => current === "fill" ? "contain" : "fill")}>{objectFitMode === "fill" ? t("클래스_모니터_화면_비율_CONTAIN") : t("클래스_모니터_화면_비율_FILL")}</button>
                {viewMode === "group" && <select value={groupIndex} onChange={event => setGroupIndex(Number(event.target.value))}>{groups.map((group, index) => <option key={index} value={index}>{t("클래스_모니터_그룹_인원", { group: index + 1, count: group.length })}</option>)}</select>}
                {/* 260728 전체 학생 구간 이동을 모드 메뉴 오른쪽에 배치해 영상 영역 높이를 확보한다. */}
                {viewMode === "all" && !selected && <div className="class-monitor-pager class-monitor-nav-pager">
                    <button type="button" disabled={page === 1} onClick={() => moveAllGridPage(page - 1)}>{t("클래스_모니터_이전")}</button>
                    {Array.from({ length: pageCount }, (_, index) => <button type="button" className={page === index + 1 ? "active" : ""} key={index} onClick={() => moveAllGridPage(index + 1)}>{index + 1}</button>)}
                    <button type="button" disabled={page === pageCount} onClick={() => moveAllGridPage(page + 1)}>{t("클래스_모니터_다음")}</button>
                </div>}
            </nav>

            {/* 260728 모드 전환에도 동일 video DOM과 MediaSoup srcObject를 유지하고 CSS 배치만 바꾼다. */}
            <section className={`class-monitor-stage mode-${stageMode} group-${groupMembers.length}`} ref={allGridRef} onScroll={stageMode === "all" ? updateAllGridPage : undefined}>
                <div className="class-monitor-student-list">
                    {liveInfo.list_member.map((user, index) => renderStudent(user, index))}
                </div>
                {renderTeacher(`${isTeacherVisible ? "" : "class-monitor-hidden"}`, true, "teacher")}
            </section>
            {/* 260728 교사 영상은 학생용 해상도 모드를 사용하지 않으므로 모드 표시도 숨긴다. */}
            {(!selected || !selected.isTeacher) && <aside>{t("클래스_모니터_현재_해상도_모드", { mode: selectedMode })}</aside>}
        </main>
    );
};

export default connect(({ user, live }) => ({
    isRbmqServerConnected: user.isRbmqServerConnected,
    isReadyLiveConsume: live.isReadyLiveConsume,
    performLiveStatusInfo: live.performLiveStatusInfo,
    smartTVBindInfo: live.smartTVBindInfo
}))(WebClassMonitorPage);
